import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { shipmentTableChangeQuery, shipmentTableSetColumns } from '../shipments-table/shipments-table.actions';
import { debounceTime, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AppState } from '../index';
import { ShipmentQuery } from '../../models/shipment-query';
import {
    interfaceDocumentClear,
    interfaceDocumentReady, interfaceSetSearchResults, interfaceSetSearchTerm,
    interfaceShipmentViewChanged,
    interfaceShipmentViewSelect
} from './interface.actions';
import { PrinterService } from '../../modules/shared/services/printer.service';
import { selectShipmentViews } from '../profile/profile.state';
import { ShipmentHeaderService } from '../../modules/shared/services/shipment-header.service';
import { EntityEnum } from '../../enum/entity.enum';
import { IconEnum } from '../../enum/icon.enum';

@Injectable()
export class InterfaceEffects {

    constructor(private actions$: Actions, private store: Store<AppState>, private printerService: PrinterService,
                private shipmentHeaderService: ShipmentHeaderService) {
    }

    shipmentQueryChanged$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentTableChangeQuery),
        withLatestFrom(this.store.select(selectShipmentViews)),
        map(([ query, views ]) => {
            const view = views.find((v) => this.compareQueries(v.query, query.query));
            return interfaceShipmentViewChanged({ id: view ? view.id : null });
        })
    ));

    shipmentViewSelect$ = createEffect(() => this.actions$.pipe(
        ofType(interfaceShipmentViewSelect),
        withLatestFrom(this.store.select(selectShipmentViews)),
        map(([ action, views ]) => views.find((view) => view.id === action.id)),
        map((view) => shipmentTableChangeQuery({ query: view.query }))
    ));

    shipmentViewSelectColumns$ = createEffect(() => this.actions$.pipe(
        ofType(interfaceShipmentViewSelect),
        withLatestFrom(this.store.select(selectShipmentViews)),
        map(([ action, views ]) => views.find((view) => view.id === action.id)),
        map((view) => shipmentTableSetColumns({ columns: view.columns }))
    ));

    interfaceDocumentReady$ = createEffect(() => this.actions$.pipe(
        ofType(interfaceDocumentReady),
        tap((props) => this.printerService.printPdf(props.document.url)),
        map(() => interfaceDocumentClear())
    ));

    interfaceGlobalSearch$ = createEffect(() => this.actions$.pipe(
        ofType(interfaceSetSearchTerm),
        debounceTime(500),
        switchMap((action) => this.shipmentHeaderService.search(action.term)),
        map((results) => results.map((h) => ({
            entity: EntityEnum.SHIPMENT,
            route: [ 'shipment', h.id ],
            caption: h.number,
            icon: 'shipment',
            detail: h.shipper.organization + ' -> ' + h.destination.organization,
            shipment: { ...h }
        }))),
        map((results) => interfaceSetSearchResults({ results }))
    ));

    protected compareQueries(a: ShipmentQuery, b: ShipmentQuery): boolean {
        return a.filter === b.filter &&
            a.beforeDate === b.beforeDate &&
            a.afterDate === b.afterDate &&
            a.state === b.state;
    }

}
